import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import {
  alpha,
  Chip,
  Divider,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  styled,
} from "@mui/material";
import yn from "yn";
import UserAvatar from "~/components/UserAvatar";
import useUser from "~/contexts/user";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { zendeskUrl } from "~/utils/zendesk";

const { VITE_SUPPORT_EMAIL, VITE_HELP_CENTER_ENABLED } = import.meta.env;

const StyledMenuItem = styled(MenuItem)({
  padding: 0,
  a: {
    width: "100%",
    padding: "8px 16px",
  },
});

const AccountMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = useUser();
  const [, updatePreferences] = useUserPreferences();
  const showManifoldNextGen = useFliptBoolean("manifold-nextgen", false, user?.email);
  const isNextGenView = pathname.startsWith(ROUTE_NEXTGEN_VIEW);

  const handleClose = () => setAnchorEl(null);

  const handleOptIntoManifoldNextGen = () => {
    updatePreferences({ optedIntoManifoldNextGen: true });
    handleClose();
    navigate(ROUTE_NEXTGEN_VIEW);
  };

  const handleOptOutOfManifoldNextGen = () => {
    updatePreferences({ optedIntoManifoldNextGen: false });
    handleClose();
    navigate(pathname.replace(ROUTE_NEXTGEN_VIEW, "") || "/");
  };
  const helpCenterEnabled = yn(VITE_HELP_CENTER_ENABLED, { default: true });
  const newTabLinkProps = { target: "_blank", rel: "noopener noreferrer" };

  return (
    <>
      <IconButton
        id="account-button"
        aria-controls={anchorEl ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          p: 0.5,
          "&[aria-expanded='true'], :hover": { bgcolor: "rgba(255, 255, 255, 0.16)" },
        }}
      >
        <UserAvatar size={28} />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "account-button",
        }}
        PaperProps={{ sx: { borderRadius: 2 }, elevation: 2 }}
        sx={{ marginTop: 0.5, marginLeft: 1 }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {showManifoldNextGen && (
          <>
            {isNextGenView ? (
              <StyledMenuItem onClick={handleOptOutOfManifoldNextGen} sx={{ px: 2, py: 1 }}>
                <ListItemText>Switch to classic look</ListItemText>
              </StyledMenuItem>
            ) : (
              <StyledMenuItem onClick={handleOptIntoManifoldNextGen} sx={{ px: 2, py: 1 }}>
                <ListItemText>Try the new look</ListItemText>
                <Chip
                  label="Beta"
                  size="small"
                  sx={(theme) => ({
                    ml: 1,
                    height: 18,
                    fontSize: 10,
                    bgcolor: "neutral.150",
                    color: "inherit",
                    transition: "none",
                    ".MuiMenuItem-root:hover &": {
                      bgcolor: alpha(theme.palette.neutral.white, 0.25),
                    },
                  })}
                />
              </StyledMenuItem>
            )}
            <Divider sx={{ mx: 2 }} />
          </>
        )}
        {helpCenterEnabled && (
          <StyledMenuItem>
            <Link
              underline="none"
              onClick={handleClose}
              href={zendeskUrl()}
              color="inherit"
              {...newTabLinkProps}
            >
              Help Center
            </Link>
          </StyledMenuItem>
        )}
        <StyledMenuItem>
          <Link
            underline="none"
            onClick={handleClose}
            href={`${VITE_SUPPORT_EMAIL ? `mailto:${VITE_SUPPORT_EMAIL}` : zendeskUrl("/requests/new")}`}
            color="inherit"
            {...(!VITE_SUPPORT_EMAIL ? newTabLinkProps : {})}
          >
            Contact Support
          </Link>
        </StyledMenuItem>
        <Divider sx={{ mx: 2 }} />
        <StyledMenuItem>
          <Link underline="none" onClick={handleClose} href="/logout" color="inherit">
            Sign Out
          </Link>
        </StyledMenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
